import { useCurrentEditor, useEditorState } from '@tiptap/react';

import { Icons } from '@/assets/icons/icons';
import { classList } from '@/utils/cn';

import { IconButton } from '../../IconButton';
import { type EditorFeatureSet } from '../BaseEditor';
import { EditorBubbleItem } from '../components';

import type { SelectorItem } from './node';

const DEFAULT_HIGHLIGHT_COLOR = 'var(--yellow-4)';

// Marks hidden in the `email` feature set.
const EMAIL_HIDDEN_MARKS = new Set(['underline', 'highlight']);
const COMPOSER_MARKS = new Set(['bold', 'italic', 'strike', 'code']);

export const TextButtons = ({
  featureSet = 'full',
}: {
  featureSet?: EditorFeatureSet | 'composer';
}) => {
  const { editor } = useCurrentEditor();
  const activeMarks = useEditorState({
    editor,
    selector: ({ editor }) =>
      ['bold', 'italic', 'underline', 'strike', 'code', 'highlight'].filter(
        (mark) => editor?.isActive(mark)
      ),
  });

  if (!editor) return null;

  const items: SelectorItem[] = [
    {
      name: 'bold',
      isActive: (editor) => editor.isActive('bold'),
      command: (editor) => editor.chain().focus().toggleBold().run(),
      icon: Icons.bold,
      tooltip: 'Bold',
      hotkey: ['meta', 'b'],
    },
    {
      name: 'italic',
      isActive: (editor) => editor.isActive('italic'),
      command: (editor) => editor.chain().focus().toggleItalic().run(),
      icon: Icons.italic,
      tooltip: 'Italic',
      hotkey: ['meta', 'i'],
    },
    {
      name: 'underline',
      isActive: (editor) => editor.isActive('underline'),
      command: (editor) => editor.chain().focus().toggleUnderline().run(),
      icon: Icons.underline,
      tooltip: 'Underline',
      hotkey: ['meta', 'u'],
    },
    {
      name: 'strike',
      isActive: (editor) => editor.isActive('strike'),
      command: (editor) => editor.chain().focus().toggleStrike().run(),
      icon: Icons.strikeThrough,
      tooltip: 'Strike-through',
      hotkey: ['meta', 'shift', 's'],
    },
    {
      name: 'code',
      isActive: (editor) => editor.isActive('code'),
      command: (editor) => editor.chain().focus().toggleCode().run(),
      icon: Icons.code,
      tooltip: 'Mark as code',
      hotkey: ['meta', 'shift', 'c'],
    },
    {
      name: 'highlight',
      isActive: (editor) => editor.isActive('highlight'),
      command: (editor) => {
        if (editor.isActive('highlight')) {
          editor.chain().focus().unsetHighlight().run();
          return;
        }
        editor
          .chain()
          .focus()
          .setHighlight({ color: DEFAULT_HIGHLIGHT_COLOR })
          .run();
      },
      icon: Icons.highlighter,
      tooltip: 'Highlight',
      hotkey: ['meta', 'shift', 'h'],
    },
  ];
  const visibleItems = items.filter(
    (item) =>
      featureSet === 'full' ||
      (featureSet === 'email' && !EMAIL_HIDDEN_MARKS.has(item.name)) ||
      (featureSet === 'composer' && COMPOSER_MARKS.has(item.name))
  );

  return (
    <div className="flex items-center translate-y-[1px] gap-2 px-1">
      {visibleItems.map((item) => (
        <EditorBubbleItem
          key={item.name}
          onSelect={(editor) => {
            item.command(editor);
          }}
        >
          <IconButton
            icon={item.icon}
            aria-label={item.tooltip}
            size="xs"
            variant="ghost"
            className={classList('size-6 hover:bg-surface-active', {
              'text-blue-500': activeMarks?.includes(item.name),
            })}
            tooltip={item?.tooltip}
            tooltipOptions={{ hotkey: item?.hotkey, sideOffset: 9 }}
          />
        </EditorBubbleItem>
      ))}
    </div>
  );
};
